import { useCallback, useEffect, useRef, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { FileText, Link2, Plus, Trash2 } from 'lucide-react';
import { docsAPI } from '../../lib/api';
import { getUser } from '../../lib/auth';
import { useProject } from '../../layout/ProjectLayout';
import { btnGhost, btnPrimary, fmtDateTime, inputCls } from '../../components/ui';

function DocEditor({ docId, readOnly, onDeleted, onRenamed }) {
  const [doc, setDoc] = useState(null);
  const [conflict, setConflict] = useState('');
  const [savedAt, setSavedAt] = useState('');
  const baseRef = useRef('');
  const timerRef = useRef(null);

  const editor = useEditor({
    extensions: [StarterKit],
    editable: !readOnly,
    editorProps: {
      attributes: {
        class: 'pm-doc prose-invert min-h-[50vh] outline-none text-sm leading-relaxed',
      },
    },
    onUpdate: () => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(save, 1200);
    },
  });

  const save = useCallback(async () => {
    if (!editor || readOnly) return;
    try {
      const { data } = await docsAPI.put(docId, {
        content: editor.getJSON(),
        base_updated_at: baseRef.current,
      });
      baseRef.current = data.updated_at;
      setSavedAt(data.updated_at);
      setConflict('');
    } catch (err) {
      if (err.response?.status === 409) setConflict(err.response.data.detail);
    }
  }, [editor, docId, readOnly]);

  useEffect(() => {
    if (!editor) return;
    docsAPI.get(docId).then(({ data }) => {
      setDoc(data);
      baseRef.current = data.updated_at;
      setSavedAt(data.updated_at);
      editor.commands.setContent(data.content || { type: 'doc', content: [] });
    });
    return () => clearTimeout(timerRef.current);
  }, [docId, editor]);

  const share = async () => {
    const { data } = await docsAPI.share(docId);
    if (data.public_token) {
      const url = `${window.location.origin}/shared/doc/${data.public_token}`;
      await navigator.clipboard?.writeText(url);
      setDoc((d) => ({ ...d, public_token: data.public_token }));
      window.alert(`Public link copied:\n${url}`);
    } else {
      setDoc((d) => ({ ...d, public_token: null }));
      window.alert('Public link disabled.');
    }
  };

  const reloadLatest = async () => {
    const { data } = await docsAPI.get(docId);
    baseRef.current = data.updated_at;
    editor.commands.setContent(data.content || { type: 'doc', content: [] });
    setConflict('');
  };

  if (!doc) return null;

  return (
    <div className="flex h-full flex-col">
      <div className="mb-2 flex items-center gap-2">
        <input
          defaultValue={doc.title}
          disabled={readOnly}
          onBlur={async (e) => {
            const title = e.target.value.trim();
            if (title && title !== doc.title) {
              const { data } = await docsAPI.put(docId, { title, base_updated_at: baseRef.current });
              baseRef.current = data.updated_at;
              onRenamed();
            }
          }}
          className="min-w-0 flex-1 bg-transparent text-base font-semibold outline-none"
        />
        <span className="text-[10px] text-pm-faint">saved {fmtDateTime(savedAt)}</span>
        {!readOnly && (
          <>
            <button
              type="button"
              onClick={share}
              title={doc.public_token ? 'Disable public link' : 'Create public link'}
              className={`rounded p-1.5 ${doc.public_token ? 'text-pm-accent' : 'text-pm-faint hover:text-pm-text'}`}
            >
              <Link2 size={15} />
            </button>
            <button
              type="button"
              onClick={async () => {
                if (!window.confirm(`Delete "${doc.title}"?`)) return;
                await docsAPI.remove(docId);
                onDeleted();
              }}
              className="rounded p-1.5 text-pm-faint hover:text-pm-danger"
              title="Delete doc"
            >
              <Trash2 size={15} />
            </button>
          </>
        )}
      </div>
      {conflict && (
        <div className="mb-2 flex items-center gap-3 rounded-md border px-3 py-2 text-xs" style={{ borderColor: 'var(--pm-danger)', color: 'var(--pm-danger)' }}>
          {conflict}
          <button type="button" onClick={reloadLatest} className="underline">
            Load latest (discards my edits)
          </button>
          <button type="button" onClick={() => { baseRef.current = ''; save(); }} className="underline">
            Overwrite anyway
          </button>
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-pm-border bg-pm-surface p-6">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}

export default function DocsScreen() {
  const { project } = useProject();
  const [docs, setDocs] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [newTitle, setNewTitle] = useState('');
  const [adding, setAdding] = useState(false);
  const user = getUser();
  const readOnly = user?.role === 'guest';

  const load = useCallback(async () => {
    const { data } = await docsAPI.list(project.id);
    setDocs(data.docs);
    return data.docs;
  }, [project.id]);

  useEffect(() => {
    load().then((d) => d.length && setActiveId((cur) => cur || d[0].id));
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    const { data } = await docsAPI.create(project.id, { title: newTitle.trim() });
    setNewTitle('');
    setAdding(false);
    await load();
    setActiveId(data.id);
  };

  const byFolder = {};
  docs.forEach((d) => {
    (byFolder[d.folder_path || ''] = byFolder[d.folder_path || ''] || []).push(d);
  });

  return (
    <div className="flex h-full gap-4">
      <aside className="flex w-56 shrink-0 flex-col rounded-lg border border-pm-border bg-pm-surface p-2">
        <div className="mb-1 flex items-center justify-between px-1">
          <span className="text-xs font-semibold uppercase tracking-wide text-pm-faint">Docs</span>
          {!readOnly && (
            <button type="button" onClick={() => setAdding(true)} className="rounded p-1 text-pm-faint hover:text-pm-text" title="New doc">
              <Plus size={13} />
            </button>
          )}
        </div>
        {adding && (
          <form onSubmit={create} className="mb-1 px-1">
            <input
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => e.key === 'Escape' && setAdding(false)}
              placeholder="Doc title, Enter"
              autoFocus
              className={`${inputCls} py-1 text-xs`}
            />
          </form>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {Object.entries(byFolder).map(([folder, items]) => (
            <div key={folder || '_root'}>
              {folder && <div className="px-1 pt-2 text-[10px] uppercase text-pm-faint">{folder}</div>}
              {items.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => setActiveId(d.id)}
                  className={`flex w-full items-center gap-1.5 truncate rounded-md px-2 py-1.5 text-left text-sm ${
                    activeId === d.id ? 'bg-pm-raised text-pm-text' : 'text-pm-muted hover:text-pm-text'
                  }`}
                >
                  <FileText size={13} className="shrink-0" />
                  <span className="truncate">{d.title}</span>
                  {d.public_token && <Link2 size={10} className="ml-auto shrink-0 text-pm-accent" />}
                </button>
              ))}
            </div>
          ))}
          {docs.length === 0 && <p className="px-1 py-4 text-xs text-pm-faint">No docs yet.</p>}
        </div>
      </aside>
      <div className="min-w-0 flex-1">
        {activeId ? (
          <DocEditor
            key={activeId}
            docId={activeId}
            readOnly={readOnly}
            onDeleted={() => {
              setActiveId(null);
              load();
            }}
            onRenamed={load}
          />
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-pm-muted">
            {readOnly ? 'No docs.' : 'Create or select a doc.'}
          </div>
        )}
      </div>
    </div>
  );
}
